import { forwardRef, type ReactNode } from "react";
import { cn } from "@/shared/cn";
import { PanelToggle } from "@/shared/ui/primitives/PanelToggle";
import { RightPanel, type RightPanelContent } from "@/shared/ui/overlays/RightPanel";
import { FrameOpener } from "@/shared/ui/layout/FrameOpener";
import { PanelRightCloseIcon, PanelRightOpenIcon } from "@/shared/ui/layout/shell-icons";
import { WORK_TITLE_CLASS } from "@/shared/ui/layout/WorkHeadSlot";

export interface WorkAreaPanel {
  content: RightPanelContent;
  fullScreen: boolean;
  onToggleFullScreen: () => void;
  onClose: () => void;
}

export interface WorkAreaProps {
  collapsed: boolean;
  onToggle: () => void;
  labels: { open: string; collapse: string; region: string };
  title: ReactNode;
  titleClaimed: boolean;
  when?: ReactNode;
  hostRefs: {
    title: (element: HTMLElement | null) => void;
    actions: (element: HTMLElement | null) => void;
    tabs: (element: HTMLElement | null) => void;
    dialog: (element: HTMLElement | null) => void;
  };
  panel: WorkAreaPanel | null;
  dialogHosted: boolean;
  children: ReactNode;
}

export const WorkArea = forwardRef<HTMLElement, WorkAreaProps>(function WorkArea(
  {
    collapsed,
    onToggle,
    labels,
    title,
    titleClaimed,
    when,
    hostRefs,
    panel,
    dialogHosted,
    children,
  },
  ref,
) {
  return (
    <main
      ref={ref}
      aria-label={labels.region}
      data-frame="work"
      data-collapsed={collapsed || undefined}
      className={cn(
        "relative flex min-h-0 min-w-0 flex-col bg-(--z-work)",
        "transition-[flex-basis,width] duration-[280ms] ease-(--ease-frame)",
        collapsed ? "w-(--rail-w) shrink-0" : "flex-1",
      )}
    >
      {collapsed && (
        <FrameOpener label={labels.open} onClick={onToggle}>
          <PanelRightOpenIcon />
        </FrameOpener>
      )}
      <div hidden={collapsed} className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="box-content flex h-(--head-h) shrink-0 items-center justify-between gap-[12px] border-b border-(--line) pr-[16px] pl-[28px]">
          <div className="flex min-w-0 flex-col justify-center">
            <div ref={hostRefs.title} className="contents" />
            {!titleClaimed && <h1 className={WORK_TITLE_CLASS}>{title}</h1>}
          </div>
          <div className="flex shrink-0 items-center gap-[10px]">
            {when}
            <div ref={hostRefs.actions} className="flex items-center gap-[10px] empty:hidden" />
            <PanelToggle label={labels.collapse} onClick={onToggle}>
              <PanelRightCloseIcon />
            </PanelToggle>
          </div>
        </div>
        <div ref={hostRefs.tabs} className="shrink-0 border-b border-(--line) empty:hidden" />
        <div className="@container/pane relative flex min-h-0 min-w-0 flex-1">
          <div data-pane className="flex min-h-0 min-w-0 flex-1 flex-col">
            {children}
          </div>
          {(panel || dialogHosted) && (
            <div
              data-right-panel-column
              className={cn(
                "relative flex min-h-0 w-[448px] shrink-0 flex-col",
                "pt-[28px] pr-[48px] pb-(--dockclear) -ml-[24px]",
                "@max-[761px]/pane:absolute @max-[761px]/pane:top-[12px] @max-[761px]/pane:right-[16px]",
                "@max-[761px]/pane:bottom-[12px] @max-[761px]/pane:z-[6] @max-[761px]/pane:m-0",
                "@max-[761px]/pane:w-[min(420px,calc(100%-32px))] @max-[761px]/pane:p-0",
                "@max-[761px]/pane:shadow-[-20px_0_60px_rgba(0,0,0,0.12)]",
                !panel &&
                  "@max-[761px]/pane:bottom-auto @max-[761px]/pane:max-h-[calc(100dvh-180px)] @max-[761px]/pane:rounded-(--card-r)",
              )}
            >
              {panel && (
                <RightPanel
                  panel={panel.content}
                  fullScreen={panel.fullScreen}
                  onToggleFullScreen={panel.onToggleFullScreen}
                  onClose={panel.onClose}
                />
              )}
              <div
                ref={hostRefs.dialog}
                data-panel-dialog-host
                className={cn(
                  "flex min-h-0 flex-col empty:hidden",
                  panel
                    ? "absolute top-[28px] right-[48px] bottom-(--dockclear) left-0 z-[1] bg-(--z-work) @max-[761px]/pane:inset-0"
                    : "relative",
                )}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
});
